import React, { ReactNode, RefObject, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

import { Down, Search, Up } from '@icon-park/react'

import loadingImgSrc from '@/assets/images/load.gif'
import { Tokens } from '@/assets/tokens'

import { Icon } from '@/components/core'

import { SelectionOption } from '../Selection.hooks'
import {
  ComponentContainer,
  IconWrapper,
  Label,
  PortalOptionsList,
  PortalSearchContainer,
  PortalSelectorContainer,
  PortalSelectorOptions,
  SearchInput,
  SelectedItem,
  SelectionContainer,
} from './SearchableSelector.styles'
import { CleanButton } from '../Selection.subcomponents'

type Props = {
  label?: string
  labelStyle?: React.CSSProperties
  placeholder?: string
  options: SelectionOption[]
  onSelect: (value?: SelectionOption) => void
  onSearch: (search: string) => void
  selectedOption?: SelectionOption
  loading?: boolean
  required?: boolean
  children?: ReactNode
  $inverted?: boolean
}

type DropdownPositionAndSize = {
  top: number
  left: number
  width: number
  maxHeight?: number
}

const DropdownMenu: React.FC<{
  options: SelectionOption[]
  searchTerm: string
  searchInputRef: RefObject<HTMLInputElement>
  onSearch: (value: string) => void
  onSelectOption: (option?: SelectionOption) => void
  onClose: () => void
  positionAndSize: DropdownPositionAndSize
  containerRef: RefObject<HTMLDivElement>
}> = ({ options, searchTerm, searchInputRef, onSearch, onSelectOption, onClose, positionAndSize, containerRef }) => {
  const dropdownRef: RefObject<HTMLDivElement> = React.createRef()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, dropdownRef, containerRef])

  const style: React.CSSProperties = {
    top: `${positionAndSize.top || 0}px`,
    left: `${positionAndSize.left || 0}px`,
    width: `${positionAndSize.width || 0}px`,
    zIndex: 1000,
    maxHeight: positionAndSize.maxHeight ? `${positionAndSize.maxHeight}px` : undefined,
  }

  return ReactDOM.createPortal(
    <PortalSelectorContainer ref={dropdownRef} style={style}>
      <PortalSearchContainer onClick={(e) => e.stopPropagation()}>
        <Icon value={Search} size={16} color={Tokens.ColorUIPrimary} />
        <SearchInput
          ref={searchInputRef}
          type="text"
          placeholder="Pesquisar..."
          value={searchTerm}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onSearch(e.target.value)}
        />
      </PortalSearchContainer>
      <PortalOptionsList>
        {options.length > 0 ?
          options.map((option: SelectionOption) => (
            <PortalSelectorOptions key={option.value} onClick={() => onSelectOption(option)}>
              {option.text}
            </PortalSelectorOptions>
          ))
        : <PortalSelectorOptions style={{ justifyContent: 'center', cursor: 'default' }}>
            Nenhum resultado encontrado
          </PortalSelectorOptions>
        }
      </PortalOptionsList>
    </PortalSelectorContainer>,
    document.body,
  )
}

export function SearchableSelector({
  label,
  labelStyle,
  placeholder,
  options,
  onSelect,
  onSearch,
  selectedOption,
  loading = false,
  required = false,
  children,
  $inverted = false,
}: Props) {
  const ref: RefObject<HTMLDivElement> = React.createRef()
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [dropdownPositionAndSize, setDropdownPositionAndSize] = useState<DropdownPositionAndSize>({
    top: 0,
    left: 0,
    width: 0,
  })

  const calculateAndSetPosition = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const marginFromEdge = 20
      const defaultDropdownMaxHeightFromCSS = 320

      const newTop = rect.bottom + window.scrollY
      const newLeft = rect.left + window.scrollX
      let newMaxHeight = defaultDropdownMaxHeightFromCSS

      if (spaceBelow - marginFromEdge < defaultDropdownMaxHeightFromCSS) {
        newMaxHeight = Math.max(spaceBelow - marginFromEdge, 80)
      }
      setDropdownPositionAndSize({
        top: newTop,
        left: newLeft,
        width: rect.width,
        maxHeight: newMaxHeight,
      })
    }
  }, [ref])

  const handleToggleDropdown = useCallback(() => {
    if (!isOpen) {
      calculateAndSetPosition()
    }
    setIsOpen((value) => !value)
  }, [isOpen, calculateAndSetPosition])

  useEffect(() => {
    if (isOpen) {
      const handleReposition = () => {
        calculateAndSetPosition()
      }
      window.addEventListener('resize', handleReposition)
      document.addEventListener('scroll', handleReposition, true)
      return () => {
        window.removeEventListener('resize', handleReposition)
        document.removeEventListener('scroll', handleReposition, true)
      }
    }
  }, [isOpen, calculateAndSetPosition])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      setSearchTerm('')
    }
  }, [isOpen])

  function handleSearch(value: string): void {
    onSearch(value)
    setSearchTerm(value)
  }

  function handleSelection(selectedOption?: SelectionOption): void {
    onSelect(selectedOption)
    setTimeout(() => setIsOpen(false), 100)
  }

  const displayText = selectedOption?.text || placeholder || ''
  const hasValue = !!selectedOption

  return (
    <ComponentContainer ref={ref}>
      {label && (
        <Label style={labelStyle}>
          {label}
          {required && '*'}
        </Label>
      )}
      <SelectionContainer onClick={handleToggleDropdown} $inverted={$inverted}>
        {children}
        <SelectedItem $hasValue={hasValue} $placeholderColor={$inverted ? Tokens.ColorWhite : Tokens.ColorTextTertiary}>
          {displayText}
        </SelectedItem>
        {selectedOption && <CleanButton onClick={(): void => handleSelection()} />}
        {loading ?
          <img alt="loading" src={loadingImgSrc} />
        : <IconWrapper>
            <Icon value={isOpen ? Up : Down} size={16} color={$inverted ? Tokens.ColorWhite : Tokens.ColorUIPrimary} />
          </IconWrapper>
        }
      </SelectionContainer>

      {isOpen && (
        <DropdownMenu
          options={options}
          searchTerm={searchTerm}
          searchInputRef={searchInputRef}
          onSearch={handleSearch}
          onSelectOption={handleSelection}
          onClose={() => setIsOpen(false)}
          positionAndSize={dropdownPositionAndSize}
          containerRef={ref}
        />
      )}
    </ComponentContainer>
  )
}
